import { useEffect, useRef } from 'react';

/**
 * Set the browser-tab title to  `PACMaster - seg1 - seg2 - …`
 *
 * Usage:
 *   useDocumentTitle('Admin');                           → "PACMaster - Admin"
 *   useDocumentTitle('Admin', 'Orchestrator', 'Workers');→ "PACMaster - Admin - Orchestrator - Workers"
 *
 * On unmount the previous title is restored — this is critical for overlay
 * components (e.g. the inline Login shown after a session kickout) that
 * temporarily override the title while the underlying page stays mounted.
 *
 * Nested components can call this with a more specific path — the deepest
 * component that mounts last wins (React fires child effects after parent).
 */
export function useDocumentTitle(
  ...segments: (string | undefined | null | false)[]
) {
  const key = segments.filter(Boolean).join(' - ');
  const prevRef = useRef(document.title);

  useEffect(() => {
    // Capture current title before we overwrite it.
    // On the first run this equals what prevRef was initialised to;
    // on subsequent runs (dep change) it captures whatever the title was
    // (which is our own previous value, so restore is still correct).
    prevRef.current = document.title;

    const parts = segments.filter(Boolean) as string[];
    document.title = parts.length
      ? `PACMaster - ${parts.join(' - ')}`
      : 'PACMaster';

    return () => {
      document.title = prevRef.current;
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
}
